import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { Smartphone, Wifi, TrendingUp, Users, AlertCircle } from 'lucide-react';
import { serviceAPI } from '../services/api';
import { DEFAULT_REPORT_MONTH, DEFAULT_REPORT_YEAR } from '../utils/reportPeriod';

const ServiceTypeDetails = ({ month, year }) => {
  const { t } = useTranslation();
  const [serviceTypes, setServiceTypes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const monthParam = month != null ? month : DEFAULT_REPORT_MONTH;
  const yearParam = year != null ? year : DEFAULT_REPORT_YEAR;

  useEffect(() => {
    fetchServiceTypes();
    // eslint-disable-next-line react-hooks/exhaustive-deps -- refetch when period changes
  }, [monthParam, yearParam]);

  const fetchServiceTypes = async () => {
    try {
      setLoading(true);
      const response = await serviceAPI.getServiceTypes({ month: monthParam, year: yearParam });
      
      // Map the API response to include the proper icon components
      const mappedServiceTypes = response.data.map(serviceType => ({
        ...serviceType,
        icon: serviceType.name === 'MBB' ? Smartphone : Wifi
      }));
      
      setServiceTypes(mappedServiceTypes);
    } catch (err) {
      console.error('Error fetching service types:', err);
      setError(t('serviceTypeDetails.loadError'));
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="space-y-6">
        <div>
          <h2 className="text-xl font-bold text-gray-900 mb-2">{t('serviceTypeDetails.title')}</h2>
          <p className="text-gray-600">{t('serviceTypeDetails.subtitle')}</p>
        </div>
        <div className="flex justify-center items-center py-12">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
          <span className="ml-3 text-gray-600">{t('serviceTypeDetails.loading')}</span>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="space-y-6">
        <div>
          <h2 className="text-xl font-bold text-gray-900 mb-2">{t('serviceTypeDetails.title')}</h2>
          <p className="text-gray-600">{t('serviceTypeDetails.subtitle')}</p>
        </div>
        <div className="bg-red-50 border border-red-200 rounded-lg p-6">
          <div className="flex items-center">
            <AlertCircle className="h-6 w-6 text-red-600 mr-3" />
            <div>
              <h3 className="text-lg font-semibold text-red-800">{t('common.errorLoadingData')}</h3>
              <p className="text-red-600 mt-1">{error}</p>
              <button
                onClick={fetchServiceTypes}
                className="mt-3 bg-red-600 text-white px-4 py-2 rounded-lg hover:bg-red-700 transition-colors"
              >
                {t('common.tryAgain')}
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (serviceTypes.length === 0) {
    return (
      <div className="space-y-6">
        <div>
          <h2 className="text-xl font-bold text-gray-900 mb-2">{t('serviceTypeDetails.title')}</h2>
          <p className="text-gray-600">{t('serviceTypeDetails.subtitle')}</p>
        </div>
        <div className="text-center py-12">
          <p className="text-gray-600">{t('serviceTypeDetails.noData')}</p>
        </div>
      </div>
    );
  }

  const formatNumber = (num) => {
    return num.toLocaleString();
  };

  const formatCurrency = (amount) => {
    if (amount >= 1000000000) {
      return `${(amount / 1000000000).toFixed(1)}B ₭`;
    } else if (amount >= 1000000) {
      return `${(amount / 1000000).toFixed(1)}M ₭`;
    }
    return new Intl.NumberFormat('lo-LA').format(amount) + ' ₭';
  };

  const getColorClasses = (color) => {
    const colors = {
      blue: {
        bg: 'bg-blue-50',
        border: 'border-blue-200',
        icon: 'text-blue-600',
        accent: 'bg-blue-500',
        text: 'text-blue-700'
      },
      green: {
        bg: 'bg-green-50',
        border: 'border-green-200',
        icon: 'text-green-600',
        accent: 'bg-green-500',
        text: 'text-green-700'
      }
    };
    return colors[color];
  };

  return (
    <div className="space-y-6">
      <div>
        <h2 className="text-xl font-bold text-gray-900 mb-2">{t('serviceTypeDetails.title')}</h2>
        <p className="text-gray-600">{t('serviceTypeDetails.subtitle')}</p>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {serviceTypes.map((service) => {
          const Icon = service.icon;
          const colors = getColorClasses(service.color);
          
          return (
            <div
              key={service.name}
              className={`${colors.bg} ${colors.border} border-2 rounded-lg p-6 hover:shadow-lg transition-shadow duration-200`}
            >
              {/* Header */}
              <div className="flex items-center justify-between mb-6">
                <div className="flex items-center space-x-3">
                  <div className={`p-3 ${colors.accent} rounded-lg`}>
                    <Icon className="h-6 w-6 text-white" />
                  </div>
                  <div>
                    <h3 className="text-xl font-bold text-gray-900">{service.name}</h3>
                    <p className="text-sm text-gray-600">{service.fullName}</p>
                  </div>
                </div>
                <span className={`text-sm font-medium px-3 py-1 rounded-full bg-white ${colors.text}`}>
                  {service.growth}
                </span>
              </div>

              {/* Key Metrics */}
              <div className="grid grid-cols-2 gap-4 mb-6">
                <div className="bg-white rounded-lg p-4">
                  <div className="flex items-center mb-2">
                    <Users className="h-4 w-4 text-gray-500 mr-2" />
                    <span className="text-sm text-gray-600">{t('common.subscribers')}</span>
                  </div>
                  <div className="text-2xl font-bold text-gray-900">
                    {formatNumber(service.subscribers)}
                  </div>
                  <div className="text-xs text-gray-500 mt-1">
                    {t('serviceTypeDetails.marketShare', { value: service.marketShare })}
                  </div>
                </div>

                <div className="bg-white rounded-lg p-4">
                  <div className="flex items-center mb-2">
                    <TrendingUp className="h-4 w-4 text-gray-500 mr-2" />
                    <span className="text-sm text-gray-600">{t('common.revenue')}</span>
                  </div>
                  <div className="text-2xl font-bold text-gray-900">
                    {formatCurrency(service.revenue)}
                  </div>
                  <div className="text-xs text-gray-500 mt-1">
                    ARPU: {new Intl.NumberFormat('lo-LA').format(service.avgArpu)} ₭
                  </div>
                </div>
              </div>

              {/* Progress Bar */}
              <div className="mb-6">
                <div className="flex justify-between text-sm text-gray-600 mb-2">
                  <span>{t('serviceTypeDetails.activeVsTotal')}</span>
                  <span>{((service.details.active / service.subscribers) * 100).toFixed(1)}%</span>
                </div>
                <div className="w-full bg-gray-200 rounded-full h-3">
                  <div
                    className={`${colors.accent} h-3 rounded-full transition-all duration-500`}
                    style={{
                      width: `${(service.details.active / service.subscribers) * 100}%`
                    }}
                  ></div>
                </div>
              </div>

              {/* Detailed Stats */}
              <div className="grid grid-cols-2 gap-3">
                <div className="bg-white rounded-lg p-3">
                  <div className="text-lg font-bold text-gray-900">
                    {formatNumber(service.details.active)}
                  </div>
                  <div className="text-xs text-gray-600">{t('serviceTypeDetails.activeUsers')}</div>
                </div>
                <div className="bg-white rounded-lg p-3">
                  <div className="text-lg font-bold text-gray-900">
                    {formatNumber(service.details.inactive)}
                  </div>
                  <div className="text-xs text-gray-600">{t('serviceTypeDetails.inactiveUsers')}</div>
                </div>
                <div className="bg-white rounded-lg p-3">
                  <div className="text-lg font-bold text-green-600">
                    +{formatNumber(service.details.newSubscribers)}
                  </div>
                  <div className="text-xs text-gray-600">{t('serviceTypeDetails.newThisMonth')}</div>
                </div>
                <div className="bg-white rounded-lg p-3">
                  <div className="text-lg font-bold text-red-600">
                    {service.details.churnRate}%
                  </div>
                  <div className="text-xs text-gray-600">{t('serviceTypeDetails.churnRate')}</div>
                </div>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
};

export default ServiceTypeDetails;